import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../../api/http.ts';
import { itemKeys } from './itemsApi.ts';
import { invalidateSuggestions } from './useItems.ts';

interface ForgetSuggestionResult {
  removed: number;
}

/**
 * Erases a name from the group's history, so it is never suggested again.
 *
 * There is no undo and nothing recorded. That is the point: a typo is not worth keeping
 * a list of, and typing the name again is quicker than finding it in a settings screen.
 */
export function useForgetSuggestion(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<ForgetSuggestionResult>(
        `/api/groups/${groupId}/suggestions?name=${encodeURIComponent(name)}`,
        { method: 'DELETE' },
      ),

    onSuccess: async () => {
      await Promise.all([
        invalidateSuggestions(queryClient),
        // Forgetting can take rows off a list too — the same name may have been sitting
        // on another list in the group — so the items themselves may be stale.
        queryClient.invalidateQueries({ queryKey: itemKeys.all }),
      ]);
    },
  });
}
